// ============================================
// Jeton JWT pour l’API Express GS (audience = app SPA)
// ============================================
//
// Les scopes par défaut incluent User.Read → l’access token a souvent
// audience Microsoft Graph. Notre serveur valide jwt avec audience =
// AZURE_AD_CLIENT_ID (l’app SPA). Il faut envoyer l’id_token (aud = clientId),
// pas l’access token Graph — sinon 401 partout.
// ============================================

import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';

const MS_GRAPH_GUID = '00000003-0000-0000-c000-000000000000';

/** Buffer secondes avant exp en dessous duquel on considère le token comme expiré.
 *  60s = marge pour l'aller-retour HTTP + clock skew côté serveur. */
const EXP_BUFFER_SEC = 60;

/**
 * Erreur typée levée quand aucun JWT valide ne peut être obtenu (silent SSO mort).
 * apiFetch wrap en ApiUnauthorizedError, React Query (createGsQueryClient) ne retry pas,
 * et les pages avec refetchInterval doivent guarder dessus pour casser le polling.
 */
export class AuthTokenExpiredError extends Error {
  readonly name = 'AuthTokenExpiredError';
  constructor(message: string) {
    super(message);
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (b64.length % 4)) % 4;
    b64 += '='.repeat(pad);
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Vrai si le token JWT est expiré (ou expire dans moins de `bufferSec`).
 * Returns true aussi si le payload est indécodable ou n'a pas de claim `exp` numérique
 * (sécurité par défaut : on ne renvoie jamais un token qu'on ne peut pas valider).
 */
export function isJwtExpired(token: string, bufferSec: number = EXP_BUFFER_SEC): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const exp = typeof payload.exp === 'number' ? payload.exp : null;
  if (exp === null) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp - nowSec < bufferSec;
}

function isMicrosoftGraphAudience(aud: string): boolean {
  if (aud === MS_GRAPH_GUID) return true;
  return aud.toLowerCase().startsWith('https://graph.microsoft.com');
}

/**
 * Choisit un JWT que notre backend peut vérifier (audience ≠ Graph) ET non expiré.
 * Priorité : id_token (OIDC), puis access token si son `aud` est le client SPA.
 *
 * Règle critique : un token expiré (ou expirant dans <60s) n'est JAMAIS retourné,
 * même en fallback. Renvoyer un token expiré déclenche un 401 backend, qui déclenche
 * un retry apiFetch avec forceRefresh, qui peut re-tomber sur le même token expiré
 * dans le cache MSAL → boucle 401 (BUG-AUTH-401-LOOP-SYNC-STATUS-RUNNING 2026-05-15).
 */
export function selectJwtForOwnBackend(
  result: AuthenticationResult,
  spaClientId: string
): string | null {
  const cid = spaClientId.toLowerCase();
  const ordered: string[] = [];
  if (result.idToken?.trim()) ordered.push(result.idToken.trim());
  if (result.accessToken?.trim()) ordered.push(result.accessToken.trim());

  for (const token of ordered) {
    if (isJwtExpired(token)) continue; // skip expired
    const payload = decodeJwtPayload(token);
    const audRaw = payload?.aud;
    if (audRaw === undefined || audRaw === null) continue;
    const audStr = Array.isArray(audRaw) ? String(audRaw[0]) : String(audRaw);
    if (isMicrosoftGraphAudience(audStr)) continue;
    const audLower = audStr.toLowerCase();
    if (audLower === cid) return token;
    if (audLower.startsWith('api://') && audLower.includes(cid)) return token;
  }

  // Fallback : idToken même si l'audience ne match pas explicitement le clientId
  // (cas Azure AD multi-tenant où aud=tenantId au lieu de clientId). Mais TOUJOURS
  // vérifier qu'il n'est pas expiré — sinon boucle 401 garantie.
  const fallback = result.idToken?.trim();
  if (fallback && !isJwtExpired(fallback)) return fallback;
  return null;
}

/**
 * Obtient un JWT pour `Authorization: Bearer` vers l'API GS (même audience que le serveur).
 *
 * Stratégie de récupération :
 *   1. acquireTokenSilent (cache MSAL — rapide)
 *   2. acquireTokenSilent + forceRefresh (si cache invalide)
 *   3. Si tout échoue → loginRedirect({ prompt: 'none' })
 *      (silent re-auth via cookie SSO Azure AD, sans UI ni interaction)
 *
 * @param options.forceRefresh — saute l'étape 1, va direct au refresh (utilisé par le retry 401)
 */
export async function acquireJwtForOwnBackend(
  instance: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
  spaClientId: string,
  options: { forceRefresh?: boolean } = {}
): Promise<string> {
  if (!spaClientId) {
    throw new Error('spaClientId manquant — vérifiez VITE_AZURE_AD_CLIENT_ID');
  }

  if (!options.forceRefresh) {
    try {
      const result = await instance.acquireTokenSilent({ scopes, account } as SilentRequest);
      const picked = selectJwtForOwnBackend(result, spaClientId);
      if (picked) return picked;
    } catch (e) {
      // Fall through vers forceRefresh
    }
  }

  try {
    const result = await instance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
      account,
      forceRefresh: true,
    } as SilentRequest);
    const picked = selectJwtForOwnBackend(result, spaClientId);
    if (picked) return picked;
  } catch (e) {
    // Fall through vers loginRedirect silent
  }

  // Dernière chance : silent loginRedirect via cookie SSO Azure AD.
  // Aucune UI, aucune interaction utilisateur. Si la session SSO Azure est valide,
  // l'utilisateur est rebalancé sur la page avec un token frais sans s'en rendre compte.
  try {
    await instance.loginRedirect({ scopes, prompt: 'none', account });
  } catch (err) {
    // loginRedirect({ prompt: 'none' }) peut throw InteractionRequiredAuthError
    // si la session SSO Azure est vraiment morte (ex: user a logout partout).
    // On throw AuthTokenExpiredError (typée) pour que apiFetch -> ApiUnauthorizedError ->
    // React Query stoppe les retries ET les pages avec refetchInterval cessent de poller.
    throw new AuthTokenExpiredError(
      'Session Azure expirée — nouvelle connexion requise. (original: ' +
        (err instanceof Error ? err.message : String(err)) +
        ')'
    );
  }

  // loginRedirect déclenche une navigation → le code ici n'est jamais atteint.
  // On throw quand même pour TypeScript (AuthTokenExpiredError typée).
  throw new AuthTokenExpiredError('loginRedirect en cours — cette ligne ne devrait jamais être atteinte');
}
